import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Phone, Mail, Linkedin, MessageCircle, Loader2, RotateCcw, Save } from 'lucide-react';

const CHANNELS = [
  { id: 'call', label: 'Call', icon: Phone },
  { id: 'dm', label: 'IG · FB DM', icon: MessageCircle },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'email', label: 'Email', icon: Mail },
];

export const ScriptTemplatesModal = ({ theme, api, workspaceId, onClose, onSaved }) => {
  const { isDarkMode: dark } = theme;
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [channel, setChannel] = useState('call');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/api/workspaces/${workspaceId}/leads/script-templates`);
        setData(r.data);
        const v = {};
        CHANNELS.forEach(c => { v[c.id] = r.data.templates[c.id] || r.data.defaults[c.id]; });
        setValues(v);
      } catch { toast.error('Failed to load templates'); onClose(); }
    })();
  }, [api, workspaceId, onClose]);

  const save = async () => {
    setSaving(true);
    try {
      const templates = {};
      CHANNELS.forEach(c => {
        templates[c.id] = values[c.id] === data.defaults[c.id] ? '' : values[c.id];
      });
      await api.put(`/api/workspaces/${workspaceId}/leads/script-templates`, { templates });
      toast.success('Templates saved — applied to every lead');
      onSaved();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const line = dark ? 'border-white/10' : 'border-slate-200';
  const tp = dark ? 'text-white' : 'text-slate-900';
  const ts = dark ? 'text-white/40' : 'text-slate-500';
  const isCustom = data && values[channel] !== data.defaults[channel];

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="script-templates-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-2xl rounded-2xl border ${line} ${dark ? 'bg-[#101216]' : 'bg-white'} shadow-2xl overflow-hidden`}>
        <div className={`flex items-center justify-between px-5 py-3.5 border-b ${line}`}>
          <div>
            <p className={`text-sm font-bold ${tp}`}>Edit script templates</p>
            <p className={`text-[11px] ${ts}`}>Your wording, auto-filled for every lead. Variables fill in automatically.</p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-full ${ts} ${dark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`} data-testid="script-templates-close"><X size={15} /></button>
        </div>
        {!data ? (
          <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-teal-400" /></div>
        ) : (
          <div className="p-5">
            <div className="flex items-center gap-1 mb-3 flex-wrap">
              {CHANNELS.map(c => (
                <button key={c.id} onClick={() => setChannel(c.id)}
                  className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full font-semibold transition-colors ${
                    channel === c.id
                      ? (dark ? 'bg-teal-400/15 text-teal-300' : 'bg-teal-50 text-teal-700 border border-teal-200')
                      : `${ts} ${dark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}`}
                  data-testid={`script-tpl-tab-${c.id}`}>
                  <c.icon size={11} /> {c.label}
                  {data.templates[c.id] ? <span className="w-1.5 h-1.5 rounded-full bg-teal-400" /> : null}
                </button>
              ))}
            </div>
            <textarea value={values[channel] || ''} onChange={e => setValues(v => ({ ...v, [channel]: e.target.value }))}
              className={`w-full h-[300px] px-3.5 py-3 rounded-xl border ${line} ${dark ? 'bg-black/30 text-white/85 placeholder-white/25' : 'bg-slate-50 text-slate-800'} text-xs leading-relaxed font-sans focus:outline-none focus:border-teal-400/40 resize-none`}
              data-testid="script-tpl-textarea" />
            <p className={`text-[11px] mt-2 ${ts}`}>
              {'Anything in {curly braces} fills in automatically for each lead — just write around it.'}
            </p>
            <div className="flex items-center justify-between gap-2 mt-4">
              <button onClick={() => setValues(v => ({ ...v, [channel]: data.defaults[channel] }))}
                disabled={!isCustom}
                className={`flex items-center gap-1.5 text-[11px] px-3.5 py-2 rounded-full border font-semibold ${line} ${ts} disabled:opacity-40 ${dark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                data-testid="script-tpl-reset">
                <RotateCcw size={11} /> Reset this channel to default
              </button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-1.5 text-xs px-5 py-2.5 rounded-full bg-teal-500 text-white font-bold hover:bg-teal-400 disabled:opacity-60"
                data-testid="script-tpl-save">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save templates
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptTemplatesModal;
